import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, Prompt, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { createNewPlan } from '../../pets/services/PetCRUD';
import { useDraft } from '../hooks/useDraft';
import { useQuickSale } from '../hooks/useQuickSale';
import { applyDraft } from '../services/QuickSaleCRUD';
import {
  CURRENT_FILTER,
  filterPlans,
  UPCOMING_FILTER,
} from '../utils/quickSaleFilters';
import { apiErrorMessage } from '../utils/quickSaleFormat';
import { CommercialConditions } from './CommercialConditions';
import { CustomerCard } from './CustomerCard';
import { FinalizeModal } from './FinalizeModal';
import { OrdersCard } from './OrdersCard';
import { PetCard } from './PetCard';
import { PlansCard } from './PlansCard';
import { RecipesCards } from './RecipesCards';
import { ShippingCard } from './ShippingCard';
import { TransitionCard } from './TransitionCard';

/**
 * "Venta rápida": everything a sales agent needs on one page, one tab per pet.
 * Edits go to a draft that the backend prices as they are made, and
 * "Guardar cambios" applies the whole draft at once.
 */
const QuickSale: FC = () => {
  const intl = useIntl();
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const { data, loading, reload } = useQuickSale(userId);
  const [petId, setPetId] = useState<number | null>(null);
  const [planId, setPlanId] = useState<number | null>(null);
  const [planFilter, setPlanFilter] = useState(CURRENT_FILTER);
  const [orderFilter, setOrderFilter] = useState(UPCOMING_FILTER);
  const [saving, setSaving] = useState(false);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [customEditorOpen, setCustomEditorOpen] = useState(false);

  const pets = data?.pets || [];
  const pet = pets.find((item) => item.id === petId) || pets[0] || null;
  const allPetPlans = (data?.plans || []).filter(
    (plan) => plan.pet_id === pet?.id
  );
  const plans = filterPlans(allPetPlans, planFilter);
  const plan = plans.find((item) => item.id === planId) || plans[0] || null;
  const draftState = useDraft(userId, pet?.id ?? null, plan?.id ?? null, data);
  const { draft, update, isDirty, simulation, simulating, errors } = draftState;

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    // Closing the tab or reloading would lose the draft.
    const warn = (event: any) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  if (!data) {
    return (
      <div className="card p-10 text-center text-gray-600">
        {loading ? (
          <span className="spinner-border text-primary" />
        ) : (
          intl.formatMessage({ id: 'quickSale.loadError' })
        )}
      </div>
    );
  }

  const user = data.user;
  const viewPet = simulation?.pet ?? pet;
  const viewPlan = simulation ? simulation.plan : plan;
  const addressId =
    draft.plan?.wallet_shipping_address ??
    plan?.wallet_shipping_address_id ??
    user.shipping_addresses[0]?.id;
  const address =
    user.shipping_addresses.find((item) => item.id === addressId) || null;
  const notes = draft.user?.notes ?? user.notes ?? '';

  function confirmDiscard() {
    return (
      !isDirty ||
      window.confirm(intl.formatMessage({ id: 'quickSale.discardChanges' }))
    );
  }

  function selectPet(nextPetId: number) {
    if (nextPetId !== pet?.id && confirmDiscard()) {
      setPetId(nextPetId);
      setPlanId(null);
    }
  }

  function selectPlan(nextPlanId: number) {
    if (nextPlanId !== plan?.id && confirmDiscard()) {
      setPlanId(nextPlanId);
    }
  }

  async function save(): Promise<boolean> {
    if (!isDirty || !pet) {
      return true;
    }
    setSaving(true);
    try {
      await applyDraft(userId, {
        pet_id: pet.id,
        plan_id: plan?.id ?? null,
        draft,
      });
      draftState.reset();
      toast.success(intl.formatMessage({ id: 'quickSale.saved' }));
      await reload();
      return true;
    } catch (error) {
      toast.error(
        apiErrorMessage(
          error,
          intl.formatMessage({ id: 'quickSale.saveError' })
        )
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function finalize() {
    if (await save()) {
      setFinalizing(true);
    }
  }

  async function onCreatePlan() {
    if (!pet) {
      return;
    }
    setCreatingPlan(true);
    try {
      await createNewPlan(pet.id);
      toast.success(intl.formatMessage({ id: 'quickSale.planCreated' }));
      setPlanFilter(CURRENT_FILTER);
      await reload();
    } catch (error) {
      toast.error(
        apiErrorMessage(
          error,
          intl.formatMessage({ id: 'quickSale.genericError' })
        )
      );
    } finally {
      setCreatingPlan(false);
    }
  }

  return (
    <>
      <Prompt
        when={isDirty}
        message={intl.formatMessage({ id: 'quickSale.discardChanges' })}
      />

      <div className="d-flex flex-wrap align-items-center gap-2 mb-5">
        {pets.map((item) => (
          <button
            key={item.id}
            type="button"
            className={clsx(
              'btn btn-sm',
              item.id === pet?.id ? 'btn-primary' : 'btn-light'
            )}
            onClick={() => selectPet(item.id)}
          >
            <i className="fas fa-paw me-2" />
            {item.name}
            {!!item.is_priority_pet && <i className="fas fa-star ms-2" />}
          </button>
        ))}
        <Link
          to={`/quiz-agente?email=${encodeURIComponent(user.email)}`}
          className="btn btn-sm btn-light-primary"
        >
          <i className="fas fa-plus me-1" />
          {intl.formatMessage({ id: 'quickSale.newPet' })}
        </Link>
        {(loading || simulating) && (
          <span className="spinner-border spinner-border-sm text-primary ms-2" />
        )}
        {simulation && (
          <span className="badge badge-light-primary ms-2">
            {intl.formatMessage({ id: 'quickSale.simulationNotSaved' })}
          </span>
        )}
      </div>

      {!!errors.length && (
        <div className="alert alert-danger">
          {errors.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )}

      {!pet ? (
        <div className="card p-10 text-center text-gray-600">
          {intl.formatMessage({ id: 'quickSale.noPets' })}
        </div>
      ) : (
        <div className="row g-5">
          <div className="col-xl-3">
            <CustomerCard
              user={user}
              address={address}
              draftState={draftState}
            />
            <PetCard
              user={user}
              pet={viewPet}
              savedPet={pet}
              plan={viewPlan}
              savedPlan={plan}
              petsCount={pets.length}
              draftState={draftState}
            />
          </div>
          <div className="col-xl-9">
            <PlansCard
              userId={userId}
              pet={pet}
              plans={plans}
              allPetPlans={allPetPlans}
              selectedPlan={plan}
              viewPlan={viewPlan}
              filter={planFilter}
              onFilterChange={(filter) => {
                if (confirmDiscard()) {
                  setPlanFilter(filter);
                  setPlanId(null);
                }
              }}
              onSelectPlan={selectPlan}
              onCreatePlan={onCreatePlan}
              creatingPlan={creatingPlan}
              customEditorOpen={customEditorOpen}
              onCustomEditorOpenChange={setCustomEditorOpen}
              draftState={draftState}
            />
            {plan && viewPlan && (
              <>
                <OrdersCard
                  key={plan.id}
                  userId={userId}
                  petId={pet.id}
                  storeId={user.store.id}
                  plan={viewPlan}
                  addresses={user.shipping_addresses}
                  isSimulation={!!simulation}
                  filter={orderFilter}
                  onFilterChange={setOrderFilter}
                  onChanged={reload}
                />
                <div className="row g-5 mb-5">
                  <div className="col-lg-6">
                    <TransitionCard
                      user={user}
                      savedPet={pet}
                      plan={viewPlan}
                      savedPlan={plan}
                      draftState={draftState}
                    />
                  </div>
                  <div className="col-lg-6">
                    <ShippingCard
                      userId={userId}
                      plan={viewPlan}
                      savedPlan={plan}
                      addresses={user.shipping_addresses}
                      address={address}
                      draftState={draftState}
                      onCouponApplied={reload}
                    />
                  </div>
                </div>
                {plan.is_detailed && (
                  <RecipesCards
                    petName={pet.name}
                    plan={viewPlan}
                    savedPlan={plan}
                    draftState={draftState}
                    onEditCustom={() => setCustomEditorOpen(true)}
                  />
                )}
              </>
            )}
            <CommercialConditions user={user} />
          </div>
        </div>
      )}

      <div className="position-sticky bottom-0 bg-body border-top py-4 mt-5 d-flex flex-wrap align-items-center gap-4 z-index-2">
        <span className="fw-bolder fs-5">
          <i className="fas fa-file-alt me-2 text-gray-800" />
          {intl.formatMessage({ id: 'quickSale.notes' })}
        </span>
        <textarea
          rows={1}
          className={clsx(
            'form-control flex-grow-1 w-auto',
            draft.user?.notes !== undefined && 'border-primary'
          )}
          value={notes}
          placeholder={intl.formatMessage({ id: 'quickSale.notesPlaceholder' })}
          onChange={(event) =>
            update('user', {
              notes:
                event.target.value === (user.notes ?? '')
                  ? undefined
                  : event.target.value,
            })
          }
        />
        <button
          type="button"
          className="btn btn-light"
          disabled={!isDirty || saving}
          onClick={save}
        >
          {saving && <span className="spinner-border spinner-border-sm me-2" />}
          {intl.formatMessage({ id: 'quickSale.saveChanges' })}
        </button>
        {plan?.state === 'proposal' && (
          <button
            type="button"
            className="btn btn-success"
            disabled={saving || !!errors.length}
            onClick={finalize}
          >
            {intl.formatMessage({ id: 'quickSale.finalize' })}
            <i className="fas fa-arrow-right ms-2" />
          </button>
        )}
      </div>

      {finalizing && plan && (
        <FinalizeModal
          user={user}
          plan={plan}
          onClose={() => setFinalizing(false)}
          onConfirmed={reload}
        />
      )}
    </>
  );
};

export { QuickSale };
